import {useQuery} from '@aboutyou/data-fetcher';
import {Log} from '@aboutyou/mlm';
import {useDefaultCookieDomain} from '@aboutyou/storage';
import {doAsync, removeCookie} from '@aboutyou/utils';
import {v4 as uuidv4} from 'uuid';
import {SESSION_CACHE_KEY} from '../../CacheKeys';
import {
  getUserDataFromCookies,
  setUserDataToCookies,
} from '../../utils/read-write-user-data';
import {BroadcastUserDataToTabs} from '../SyncSessionBetweenTabs';
import {createDataStore} from '../createDataStore';
import {TADARIDA_SESSION_PERSIST_KEY} from '../userStore/constants';
import {SetSessionIdError} from './errors';

export type SessionState = {sessionId: string} | null;

const useExpensiveSessionId = () => {
  const cookieDomain = useDefaultCookieDomain();

  const query = useQuery(
    SESSION_CACHE_KEY,
    async (): Promise<SessionState> => {
      const sessionDataFromCookies = getUserDataFromCookies<SessionState>({
        storageKey: TADARIDA_SESSION_PERSIST_KEY,
        cookieDomain,
      });

      if (sessionDataFromCookies) {
        return sessionDataFromCookies;
      }

      let sessionId = '';

      if (process.env.NODE_ENV === 'test') {
        sessionId = 'test-session-id';
      } else {
        let _sessionId;
        // the browser support for crypto.randomUUID is not great, so we use uuid as fallback
        if (typeof crypto !== 'undefined' && crypto?.randomUUID) {
          _sessionId = crypto.randomUUID();
        } else {
          _sessionId = uuidv4();
        }
        sessionId = _sessionId.replace(/-/g, ''); // remove dashes from the generated id to make it compatible with the mAPI, this also works with desktop(iRIS)
      }

      if (sessionId) {
        return {sessionId};
      }
      throw new SetSessionIdError('Failed setting SessionId');
    },
    {
      staleTime: Infinity,
      onError: onGenerateSessionIdError,
      onSuccess: sessionState => {
        // the session id is saved to `__SESSION_ID__` only to make some e2e tests easier, where we prefetch or prefill the basket/wishlist
        // for a given session id and then compare to what is rendered on the page.
        // Do not use window.__SESSION_ID__ for anything features related.
        // hence its not typed.
        // @ts-expect-error intentionally not typed
        window.__SESSION_ID__ = sessionState?.sessionId;
      },
    },
  );

  // the session update is normally when the user logs in or logs out, or when session validation on page load happens.
  // In cases of these intentional updates, we want to persist the session to local storage. so that the same session can be retrieved on page reload.
  const updateCache = (sessionState: SessionState) => {
    query.updateCache(sessionState);
    persistSessionState({sessionState, cookieDomain});
  };

  return {...query, updateCache};
};

const {Provider, useData: useSessionId} = createDataStore<SessionState>({
  defaultValue: null,
  useQueryHook: useExpensiveSessionId,
});

export const usePersistSessionStateCallback = () => {
  const {data} = useSessionId();
  const cookieDomain = useDefaultCookieDomain();

  return () => {
    if (data) {
      persistSessionState({sessionState: data, cookieDomain});
    }
  };
};

const persistSessionState = ({
  sessionState,
  cookieDomain,
}: {
  sessionState: SessionState;
  cookieDomain?: string;
}) => {
  BroadcastUserDataToTabs.broadcastData({
    key: TADARIDA_SESSION_PERSIST_KEY,
    data: sessionState,
  });

  doAsync(() => {
    if (sessionState) {
      setUserDataToCookies({
        storageKey: TADARIDA_SESSION_PERSIST_KEY,
        data: sessionState,
        cookieDomain,
      });
    }
  });
};

export const cleanUpSessionState = ({cookieDomain}: {cookieDomain?: string}) =>
  removeCookie({name: TADARIDA_SESSION_PERSIST_KEY, domain: cookieDomain});

const onGenerateSessionIdError = (error: unknown) => {
  Log.error('@aboutyou/feature', 'Failed getting a SessionId', {
    error: error as Error,
  });
};

export {Provider as SessionProvider, useSessionId};
